import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Drawer } from "@material-ui/core";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { getErrorMessage, parseTimeStamp } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import CaseService from "../../../services/CaseService";
import moment from "moment";
import MoreOrLess from "../../common/MoreOrLess";
import { CustomInputField } from "../../common/FormInputs";
import { PrimaryCTAButton } from "../../common/Buttons";
import _ from "lodash";
import queryString from "query-string";
import { sanitize } from "dompurify";

export default function ViewNotesModal({
  modal,
  setModal,
  id,
  type = "Mediator",
  disabled = false,
  viewOnly,
}) {
  const [notes, setNotes] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [note, setNote] = useState("");
  const ref = useRef();

  const addNotes = async (value) => {
    try {
      setLoader({ state: true, message: "Adding notes..." });
      const payload = {
        content: value,
      };
      const res = await CaseService.addNotes(payload, id);
      if (res?.message) {
        enqueueSnackbar(res?.message, {
          variant: "success",
        });
        notes.unshift(res?.newNote);
        setNote("");
        ref.current.scrollTop = 0;
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  useEffect(() => {
    async function getAllNotes(id) {
      try {
        setLoader({ state: true, message: "loading Notes..." });
        let params = {
          page: 1,
          perPage: 1000,
        };
        let stringParams = "";
        if (!_.isEmpty(params)) {
          stringParams = `?${queryString.stringify(params)}`;
        }
        const res = await CaseService.getNotes(id, stringParams);
        if (res?.data?.length) {
          setNotes(res?.data);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
    if (id && modal) {
      getAllNotes(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modal]);

  return (
    <Drawer
      anchor={"right"}
      open={modal}
      onClose={() => setModal(false)}
      disableRestoreFocus={true}
    >
      <DrawerContainer role="presentation">
        <HeadingContainer>
          <HeaderContent>
            <HeaderText>
              <Heading>Scratch Pad</Heading>
              <NoteCount>
                {notes?.length || 0} {notes?.length === 1 ? "note" : "notes"}
              </NoteCount>
            </HeaderText>
          </HeaderContent>
          <CloseButton
            onClick={() => setModal(false)}
            aria-label="Close drawer"
          >
            <img
              src={require("../../../assets/images/closeModal.svg")}
              alt="close"
            />
          </CloseButton>
        </HeadingContainer>

        <NoteContainer ref={ref}>
          {notes?.length > 0 ? (
            <NotesWrapper>
              {notes?.map((note, index) => (
                <NoteCard key={note?.id || index}>
                  <NoteHeader>
                    <TimeStamp>
                      <TimeText>
                        {moment(parseTimeStamp(note?.created_at)).format(
                          "DD MMM YYYY",
                        )}
                        <TimeSeparator>•</TimeSeparator>
                        {moment(parseTimeStamp(note?.created_at)).format(
                          "hh:mm A",
                        )}
                      </TimeText>
                    </TimeStamp>
                  </NoteHeader>
                  <NoteContent>
                    <MoreOrLess
                      Paragraph={
                        <ContentText
                          dangerouslySetInnerHTML={{
                            __html: sanitize(note?.content),
                          }}
                        />
                      }
                      className="content"
                    />
                  </NoteContent>
                </NoteCard>
              ))}
            </NotesWrapper>
          ) : (
            <EmptyStateContainer>
              <EmptyMsg>No notes yet</EmptyMsg>
              <EmptySubtext>
                Start adding notes to keep track of important information
              </EmptySubtext>
            </EmptyStateContainer>
          )}
        </NoteContainer>

        {viewOnly !== true && (
          <EditorContainer>
            <EditorWrapper>
              <EditorLabel>
                <LabelText>Add a new note</LabelText>
              </EditorLabel>
              <InputWrapper>
                <CustomInputField
                  variant="outlined"
                  className={"input-height-auto"}
                  multiline={true}
                  rows={4}
                  placeholder="Type your note here..."
                  maxRows={4}
                  name={"note"}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </InputWrapper>
              <ButtonWrapper>
                <PrimaryCTAButton
                  onClick={() => addNotes(note)}
                  disabled={!note?.trim()}
                >
                  Add Note
                </PrimaryCTAButton>
              </ButtonWrapper>
            </EditorWrapper>
          </EditorContainer>
        )}
      </DrawerContainer>
    </Drawer>
  );
}

const DrawerContainer = styled.div`
  width: 600px;
  height: 100%;
  background: linear-gradient(to bottom, #fafafa 0%, #ffffff 100%);
  outline: none;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100vw;
  }
`;

const HeadingContainer = styled.div`
  padding: 24px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border-bottom: 2px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 20px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  line-height: 1.2;
`;

const NoteCount = styled.span`
  font-size: 13px;
  color: #666;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e8e8;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const NoteContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
`;

const NotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NoteCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 18px 20px;
  border: 2px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 131, 140, 0.12);
    transform: translateX(-4px);
  }
`;

const NoteHeader = styled.div`
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const TimeStamp = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimeText = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.INPUT_LABEL};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimeSeparator = styled.span`
  color: #ccc;
`;

const NoteContent = styled.div`
  & .content {
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.6;
    color: ${COLORS.COLOR_DARK};
    word-break: break-word;

    & .readMoreText {
      color: ${COLORS?.BTN_GREEN} !important;
      font-weight: 600;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ContentText = styled.div`
  p {
    margin: 8px 0;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 48px 24px;
  text-align: center;
`;

const EmptyMsg = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-weight: 700;
  font-size: 22px;
  margin-bottom: 12px;
  font-family: ${theme.fonts.primaryFontBold};
`;

const EmptySubtext = styled.div`
  color: #999;
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  max-width: 300px;
  line-height: 1.5;
`;

const EditorContainer = styled.div`
  background: #ffffff;
  border-top: 2px solid #f0f0f0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
`;

const EditorWrapper = styled.div`
  padding: 20px 24px;
`;

const EditorLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const LabelText = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
`;

const InputWrapper = styled.div`
  margin-bottom: 12px;

  & .MuiOutlinedInput-root {
    border-radius: 10px;
    background: #fafafa;
    transition: all 0.2s ease;

    &:hover {
      background: #f5f5f5;
    }

    &.Mui-focused {
      background: #ffffff;
      box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.1);
    }
  }

  & .MuiOutlinedInput-notchedOutline {
    border: 2px solid #e8e8e8 !important;
    transition: all 0.2s ease;
  }

  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: #d0d0d0 !important;
  }

  & .MuiOutlinedInput-multiline {
    padding: 12px 14px !important;
  }

  & textarea {
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 14px;
    line-height: 1.5;
    color: ${COLORS.COLOR_DARK};

    &::placeholder {
      color: #999;
      opacity: 1;
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  button {
    min-width: 120px;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 131, 140, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
